import { DetailViewModel } from "@frui.ts/datascreens/dist";
import { bound } from "@frui.ts/helpers/dist";
import type { interfaces } from "inversify";
import ExemptionRepository from "repositories/exemptionRepository";
import type ExemptionDetailGroupsResponse from "entities/exemptionDetailGroupsResponse";
import LocalizationService from "services/localizationService";
import type { EntityNames } from "config";
import { Modules } from "config";
import { computed } from "mobx";
import type ExemptionDetailResponse from "entities/exemptionDetailResponse";
import type { PropertyName } from "@frui.ts/helpers/dist";
import type Vehicle from "entities/vehicleResponse";
import EnumService from "services/enumService";
import VehicleRegisterVehicleStatus from "entities/vehicleRegisterVehicleStatus";
import VehicleRegisterSynchronizationStatus from "entities/vehicleRegisterSynchronizationStatus";
import ExemptionRegistrationMethod from "entities/exemptionRegistrationMethod";
import type VehicleRegisterVehicleDetailResponse from "entities/vehicleRegisterVehicleDetailResponse";
import type VehicleRegisterVehicleFuelType from "entities/vehicleRegisterVehicleFuelType";
import VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import ChargeChangeInitiator from "entities/chargeChangeInitiator";
import { isBefore } from "date-fns";
import { emptyValue } from "utils/emptyValue";
export default class ExemptionDetailViewModel extends DetailViewModel<ExemptionDetailGroupsResponse> {
  protected readonly modelName: EntityNames = "exemptionDetail";

  constructor(
    protected exemptionId: string,
    protected localizationService: LocalizationService,
    protected repository: ExemptionRepository,
    protected enumService: EnumService
  ) {
    super();
    this.navigationName = "";
  }

  onInitialize(): Promise<void> {
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    return super.onDeactivate(isClosing);
  }

  protected async loadDetail() {
    return await this.repository.getOne(this.exemptionId);
  }

  @computed
  get name() {
    return this.localizationService.translateModule(Modules.callCenter, "exemptionDetail.name");
  }

  @bound
  isValid(date: Date) {
    return !isBefore(date, new Date());
  }

  @bound
  onClose() {
    this.requestClose();
  }

  @bound
  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.callCenter, `exemptionDetail.${code}`, size);
  }

  translateSubAttribute(model: "exemptionDetailResponse", attribute: PropertyName<ExemptionDetailResponse>): string;
  translateSubAttribute(
    model: "vehicleRegisterVehicleDetailResponse",
    attribute: PropertyName<VehicleRegisterVehicleDetailResponse>
  ): string;
  translateSubAttribute(model: "vehicleResponse", attribute: PropertyName<Vehicle>): string;

  @bound
  translateSubAttribute(model: string, attribute: string) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  @bound
  translateExemptionReasonId(exemptionReasonId: string) {
    const matchingExemptionReason = this.getExemptionReason(exemptionReasonId);
    return matchingExemptionReason?.description ?? emptyValue;
  }

  @bound
  getExemptionReason(exemptionReasonId: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");

    return exemptionReasons.find(x => x.id === exemptionReasonId);
  }

  @bound
  translateCountryId(countryId: string) {
    return this.enumService.value("countries", countryId)?.name ?? emptyValue;
  }

  @bound
  translateVehicleRegisterSynchronizationStatus(status: VehicleRegisterSynchronizationStatus) {
    return this.localizationService.translateEnum("vehicleRegisterSynchronizationStatus", status);
  }

  @bound
  translateVehicleStatus(status: VehicleRegisterVehicleStatus) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleStatus", status);
  }

  @bound
  translateExemptionRegistrationMethod(exemptionRegistrationMethod?: ExemptionRegistrationMethod) {
    if (!exemptionRegistrationMethod) {
      return emptyValue;
    }
    return this.localizationService.translateEnum("exemptionRegistrationMethod", exemptionRegistrationMethod);
  }

  @bound
  translateVehicleRegisterVehicleFuelType(fuelType: VehicleRegisterVehicleFuelType[]) {
    const translation = fuelType.map(f => this.localizationService.translateEnum("vehicleRegisterVehicleFuelType", f));
    return translation.length > 1 ? translation.join(", ") : translation[0];
  }

  @bound
  translateVehicleRegisterVehicleClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  @bound
  translateChargeChangeInitiator(chargeChangeInitiator: ChargeChangeInitiator) {
    return this.localizationService.translateEnum("chargeChangeInitiator", chargeChangeInitiator);
  }

  @bound
  formatHeight(amount: number) {
    return `${amount} kg`;
  }

  static Factory({ container }: interfaces.Context) {
    return (exemptionId: string) =>
      new ExemptionDetailViewModel(
        exemptionId,
        container.get(LocalizationService),
        container.get(ExemptionRepository),
        container.get(EnumService)
      );
  }
}
